import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { findings as findingsApi, users as usersApi } from "../lib/api";
import { toast } from "sonner";
import {
  MessageSquare, Plus, Trash2, Send, Loader2, CheckCircle2, XCircle,
  Users as UsersIcon, Search, Check,
} from "lucide-react";

export default function SmsDialog({ open, onOpenChange, finding, defaultLink = "" }) {
  const { t } = useTranslation();
  const [phones, setPhones] = useState([""]);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  // Team picker state
  const [team, setTeam] = useState([]);          // [{id, name, role, phone}]
  const [teamLoading, setTeamLoading] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!open) return;
    setPhones([""]);
    setResult(null);
    setSearch("");
    const intro = `[Canales] Hallazgo en ${finding?.store_name || ""}: ${finding?.description || ""}`;
    const link = defaultLink ? `\nLink: ${defaultLink}` : "";
    setMessage(`${intro}${link}`.slice(0, 320));
  }, [open, finding, defaultLink]);

  // Load team list once the dialog opens. We use /users/assignable which is
  // permitted for non-admins too; admins get the full list back.
  useEffect(() => {
    if (!open) return;
    setTeamLoading(true);
    usersApi
      .assignable()
      .then((rows) => setTeam((rows || []).filter((u) => u.phone)))
      .catch(() => setTeam([]))
      .finally(() => setTeamLoading(false));
  }, [open]);

  const addRow = () => setPhones((p) => [...p, ""]);
  const removeRow = (i) => setPhones((p) => p.filter((_, idx) => idx !== i));
  const setRow = (i, v) => setPhones((p) => p.map((x, idx) => (idx === i ? v : x)));

  const cleanPhones = useMemo(
    () => phones.map((p) => (p || "").trim()).filter(Boolean),
    [phones],
  );
  // Set of already-added phones (normalized to "digits only") so the team
  // picker can show a "Selected" check on chips that are already in the list.
  const phoneDigitSet = useMemo(() => {
    const s = new Set();
    for (const p of cleanPhones) s.add(p.replace(/\D/g, ""));
    return s;
  }, [cleanPhones]);

  const togglePersonPhone = (person) => {
    const digits = (person.phone || "").replace(/\D/g, "");
    if (!digits) return;
    if (phoneDigitSet.has(digits)) {
      // Remove every row whose digits match this person's phone.
      setPhones((arr) => {
        const next = arr.filter((row) => row.replace(/\D/g, "") !== digits);
        return next.length === 0 ? [""] : next;
      });
      return;
    }
    setPhones((arr) => {
      // Drop empty trailing rows and append a new row with the picked phone.
      const compact = arr.filter((row) => (row || "").trim());
      if (compact.length >= 10) {
        toast.warning(t("sms.max_recipients", { n: 10 }));
        return arr;
      }
      return [...compact, person.phone];
    });
  };

  const filteredTeam = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return team;
    return team.filter((u) =>
      (u.name || "").toLowerCase().includes(q) ||
      (u.email || "").toLowerCase().includes(q) ||
      (u.role || "").toLowerCase().includes(q),
    );
  }, [team, search]);

  const canSend = cleanPhones.length > 0 && message.trim().length > 0 && !sending;

  const send = async () => {
    if (!finding) return;
    setSending(true);
    setResult(null);
    try {
      const r = await findingsApi.sendSms(finding.id, {
        phones: cleanPhones,
        message: message.trim(),
      });
      setResult(r);
      if (r.ok_count > 0) toast.success(t("sms.sent_n", { n: r.ok_count }));
      if (r.fail_count > 0) toast.warning(t("sms.failed_n", { n: r.fail_count }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("sms.send_error"));
    } finally {
      setSending(false);
    }
  };

  const charsLeft = 1500 - message.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="sms-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2">
            <MessageSquare className="text-emerald-600" size={20} />
            {t("sms.title")}
          </DialogTitle>
          <p className="text-xs text-stone-500">{t("sms.subtitle")}</p>
        </DialogHeader>

        {finding && (
          <div className="rounded-lg bg-stone-50 px-3 py-2 text-xs text-stone-600 border border-stone-200">
            <div className="font-semibold text-stone-700">{finding.store_name}</div>
            <div className="line-clamp-2">{finding.description}</div>
          </div>
        )}

        {/* === Pick from team === */}
        <div className="mt-3">
          <div className="flex items-center justify-between">
            <label className="canales-overline flex items-center gap-1.5">
              <UsersIcon size={12} /> {t("sms.choose_team")}
            </label>
            <span className="text-[11px] text-stone-400">
              {teamLoading ? t("common.loading") : t("sms.team_count", { n: team.length })}
            </span>
          </div>

          {team.length > 0 && (
            <div className="relative mt-2">
              <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-stone-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t("sms.team_search")}
                className="pl-7 h-8 text-sm"
                data-testid="sms-team-search"
              />
            </div>
          )}

          {!teamLoading && team.length === 0 && (
            <div className="mt-2 rounded-lg border border-dashed border-stone-300 bg-stone-50 px-3 py-2 text-[12px] text-stone-500">
              {t("sms.team_empty")}
            </div>
          )}

          {filteredTeam.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5 max-h-32 overflow-y-auto pr-1" data-testid="sms-team-list">
              {filteredTeam.map((u) => {
                const digits = (u.phone || "").replace(/\D/g, "");
                const selected = phoneDigitSet.has(digits);
                return (
                  <button
                    key={u.id}
                    type="button"
                    onClick={() => togglePersonPhone(u)}
                    data-testid={`sms-team-chip-${u.id}`}
                    className={`group inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[12px] border transition-all ${
                      selected
                        ? "bg-emerald-50 border-emerald-300 text-emerald-700"
                        : "bg-white border-stone-200 text-stone-700 hover:border-[#0B1220] hover:bg-stone-50"
                    }`}
                  >
                    {selected ? (
                      <Check size={11} className="text-emerald-600" />
                    ) : (
                      <Plus size={11} className="text-stone-400 group-hover:text-[#0B1220]" />
                    )}
                    <span className="font-medium">{u.name}</span>
                    <span className="text-stone-400 text-[10.5px]">· {t(`roles.${u.role}`, u.role)}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* === Manual phones list === */}
        <div className="mt-4">
          <div className="flex items-center justify-between">
            <label className="canales-overline">{t("sms.recipients")}</label>
            <span className="text-[11px] text-stone-400">
              {cleanPhones.length}/10 · {t("sms.format_hint")}
            </span>
          </div>
          <div className="mt-2 space-y-2">
            {phones.map((p, i) => (
              <div key={i} className="flex items-center gap-2">
                <Input
                  value={p}
                  onChange={(e) => setRow(i, e.target.value)}
                  placeholder={t("sms.placeholder")}
                  inputMode="tel"
                  className="tabular-nums"
                  data-testid={`sms-phone-${i}`}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => removeRow(i)}
                  disabled={phones.length <= 1}
                  className="shrink-0"
                  data-testid={`sms-remove-${i}`}
                >
                  <Trash2 size={14} />
                </Button>
              </div>
            ))}
            {phones.length < 10 && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addRow}
                className="gap-1.5"
                data-testid="sms-add-row"
              >
                <Plus size={14} /> {t("sms.add_phone")}
              </Button>
            )}
          </div>
        </div>

        <div className="mt-3">
          <div className="flex items-center justify-between">
            <label className="canales-overline">{t("sms.message")}</label>
            <span className={`text-[11px] ${charsLeft < 0 ? "text-red-600" : "text-stone-400"}`}>
              {Math.max(0, charsLeft)} {t("sms.chars_left")}
            </span>
          </div>
          <Textarea
            value={message}
            onChange={(e) => setMessage(e.target.value.slice(0, 1500))}
            placeholder={t("sms.message_placeholder")}
            className="mt-2 min-h-[100px]"
            data-testid="sms-message"
          />
        </div>

        {result && (
          <div className="mt-3 rounded-lg border border-stone-200 p-3" data-testid="sms-results">
            <div className="text-xs text-stone-500 mb-2">
              {t("sms.summary", { ok: result.ok_count, fail: result.fail_count })}
            </div>
            <ul className="space-y-1.5">
              {(result.results || []).map((r, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2 text-sm"
                  data-testid={`sms-row-${i}`}
                >
                  {r.ok ? (
                    <CheckCircle2 size={14} className="text-emerald-600 mt-0.5 shrink-0" />
                  ) : (
                    <XCircle size={14} className="text-red-600 mt-0.5 shrink-0" />
                  )}
                  <div className="min-w-0 flex-1">
                    <span className="font-mono text-xs">{r.phone}</span>
                    {r.ok ? (
                      <span className="ml-2 text-[11px] text-stone-500">{r.status || "queued"}</span>
                    ) : (
                      <span className="ml-2 text-[11px] text-red-600">{r.error}</span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={sending}>
            {t("common.close")}
          </Button>
          <Button
            onClick={send}
            disabled={!canSend}
            className="brand-button gap-1.5"
            data-testid="sms-send-button"
          >
            {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            {sending ? t("sms.sending") : t("sms.send", { n: cleanPhones.length })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
